/**
 * Загрузка профиля из Core.
 *
 * Живёт в оболочке, а не в графике, и это исправление настоящей
 * поломки. Раньше синхронизация стояла внутри терминала — то есть
 * внутри раздела «График». Пока других разделов не было, разницы не
 * существовало: график и был приложением.
 *
 * С появлением разделов вышло так, что человек, открывший «Личный
 * кабинет» и не заходивший на график, видел там не свой профиль, а
 * умолчания, — и его правки не сохранялись вовсе, потому что
 * `setSettingsPersister` тоже вызывался только в графике. Настройки
 * уведомлений при этом стоят именно в кабинете.
 *
 * Профиль принадлежит сеансу, а не разделу. Поэтому он и грузится там,
 * где начинается сеанс.
 *
 * Склад терминала подгружается по требованию, а не ввозится обычным
 * образом. Он весит под три сотни килобайт — в нём индикаторы, разметка
 * и вся машинерия графика, — и обычный ввоз затащил бы его в первый
 * экран каждому, кто открыл сводку. Ровно от этого раздел «График» и
 * сделан ленивым; свести ту работу на нет одной строкой ввоза было бы
 * обидно.
 */

import { useEffect } from 'react';
// Только как тип: обычный ввоз затащил бы модуль в первый экран.
import type { SettingsSync } from './settings-sync.js';

export function useSettingsSync(): void {
  useEffect(() => {
    let sync: SettingsSync | null = null;
    let cancelled = false;

    void (async () => {
      /*
       * Всё тяжёлое — здесь, внутри эффекта.
       *
       * Задержка на один такт загрузки ничего не стоит: разделы тоже
       * ленивые, и тот же кусок им всё равно понадобится. Зато первый
       * экран остаётся лёгким.
       */
      const [store, styles, sync_] = await Promise.all([
        import('./store.js'),
        import('../chart/styles.js'),
        import('./settings-sync.js'),
      ]);

      // Эффект успели свернуть, пока грузились куски: ходить в Core
      // незачем, а назначать сохранение — тем более.
      if (cancelled) return;

      sync = new sync_.SettingsSync({
        onState: (state) => store.useTerminal.getState().setSyncState(state),
        onLoaded: (loaded) => {
          const current = store.useTerminal.getState();

          // Индикаторы до этапа 06 жили в отдельном localStorage.
          // Переносим их в профиль один раз, чтобы настроенный график
          // не открылся пустым.
          const legacy = sync_.migrateLegacyIndicators();
          const settings =
            legacy && (loaded.workspace?.indicators.length ?? 0) === 0
              ? {
                  ...loaded,
                  workspace: {
                    venue: current.venue,
                    symbol: current.symbol,
                    timeframe: current.timeframe,
                    indicators: legacy,
                    chartStyle: styles.DEFAULT_CHART_STYLE,
                    drawings: [],
                    rules: [],
                    bots: [],
                  },
                }
              : loaded;

          const written = store.applyLoadedProfile(settings);
          if (legacy) sync_.clearLegacyIndicators();
          return written;
        },
        onConflict: () => {
          /*
           * Молчать нельзя: вкладка только что перечитала профиль и
           * отбросила то, что человек менял в ней последние секунды.
           * Без этой строки он останется в уверенности, что сохранилось.
           */
          store.useTerminal
            .getState()
            .setSyncNotice(
              'Настройки изменились в другом месте — эта вкладка перечитала их. ' +
                'Правки, сделанные здесь за последние секунды, не сохранились.',
            );
        },
      });

      store.setSettingsPersister((next) => sync?.save(next));
      store.setSettingsFlusher(() => void sync?.flush());
      void sync.load();
    })();

    /*
     * Правки с других устройств — сюда же, пока вкладка открыта:
     * при возвращении на неё и раз в полминуты, пока она на экране.
     * Удалённое на компьютере исчезает с телефона, прочитанное — гаснет.
     */
    const refresh = (): void => {
      if (document.visibilityState === 'visible') void sync?.refresh();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    const timer = setInterval(refresh, 30_000);

    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      clearInterval(timer);
      cancelled = true;
      void sync?.flush();
      sync?.dispose();
    };
  }, []);
}
